let gradientAPI = null;
let isGradientPlaybackManaged = false;
const gradientConsumers = {};

const applyGradientPlaybackState = () => {
  if (!gradientAPI || !isGradientPlaybackManaged) return;

  const shouldRun = !document.hidden && Object.values(gradientConsumers).some(Boolean);
  if (shouldRun) {
    gradientAPI.resumeGradient();
  } else {
    gradientAPI.stopGradient();
  }
};

window.setGradientConsumerState = (name, isActive) => {
  isGradientPlaybackManaged = true;
  gradientConsumers[name] = isActive;
  applyGradientPlaybackState();
};

// --- ШЕЙДЕРЫ (WebGL1) ---
const vShaderSource = `
attribute vec4 a_position;
void main() {
    gl_Position = a_position;
}
`;

const fShaderSource = `
precision highp float;

uniform vec2 u_resolution;
uniform float u_time;

uniform vec2 u_position_blob1;
uniform float u_size_blob1;
uniform float u_scale_blob1;
uniform float u_angle_blob1;
uniform float u_smooth_step_blob1;
uniform vec4 u_color_blob1;

uniform vec2 u_position_blob2;
uniform float u_size_blob2;
uniform float u_scale_blob2;
uniform float u_angle_blob2;
uniform float u_smooth_step_blob2;
uniform vec4 u_color_blob2;

uniform vec2 u_position_blob3;
uniform float u_size_blob3;
uniform float u_scale_blob3;
uniform float u_angle_blob3;
uniform float u_smooth_step_blob3;
uniform vec4 u_color_blob3;

uniform vec2 u_position_blob4;
uniform float u_size_blob4;
uniform float u_scale_blob4;
uniform float u_angle_blob4;
uniform float u_smooth_step_blob4;
uniform vec4 u_color_blob4;

uniform float u_time_multiplier;

uniform float u_deformation_ratio;
uniform float u_deformation_speed_x;
uniform float u_deformation_speed_y;
uniform float u_separation_ratio;
uniform float u_corner_smothing_ratio;
uniform float u_smooth_step;

uniform vec4 u_bg_color;
uniform float u_deform_blobs;

//
// Простая псевдослучайная функция
float noise(vec2 p) {
    return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453);
}

// Интерполированный шум (2D)
float smoothNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);

    float a = noise(i);
    float b = noise(i + vec2(1.0,0.0));
    float c = noise(i + vec2(0.0,1.0));
    float d = noise(i + vec2(1.0,1.0));
    return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
}

// Отрисовка одного blob'а
float blob(vec2 uv, vec2 center, float size, float angle, float scale_x, float smooth_step, float aspect) {
    vec2 p = uv - center;
    p.y /= aspect;

    // вращение
    float c = cos(angle);
    float s = sin(angle);
    p = vec2(p.x * c - p.y * s, p.x * s + p.y * c);

    p.x /= scale_x;
    float r = length(p);
    float radius = size;

    if (u_deform_blobs != 0.0) {
        float deformation = smoothNoise(p * u_deformation_ratio + vec2(u_time * u_deformation_speed_x, u_time * u_deformation_speed_y)) * u_separation_ratio;
        radius = size + deformation;
    }

    // мягкий край: сначала экспонента для мягкости, потом smoothstep чтобы контролировать зону
    float smoothEdge = exp(-r * r * smooth_step);
    return smoothEdge * smoothstep(radius, radius - smooth_step, r);
}

void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    uv.y = 1.0 - uv.y;
    float aspect = u_resolution.x / u_resolution.y;
    float u_time_local = u_time * u_time_multiplier;

    float shape1 = blob(uv, u_position_blob1, u_size_blob1, radians(u_angle_blob1), u_scale_blob1, u_smooth_step_blob1, aspect);
    float shape2 = blob(uv, u_position_blob2, u_size_blob2, radians(u_angle_blob2), u_scale_blob2, u_smooth_step_blob2, aspect);
    float shape3 = blob(uv, u_position_blob3, u_size_blob3, radians(u_angle_blob3), u_scale_blob3, u_smooth_step_blob3, aspect);
    float shape4 = blob(uv, u_position_blob4, u_size_blob4, radians(u_angle_blob4), u_scale_blob4, u_smooth_step_blob4, aspect);

    vec4 finalColor = u_bg_color;

    // Важно: я не менял порядок смешивания, чтобы сохранить визуал
    finalColor = mix(finalColor, u_color_blob1, shape1);
    finalColor = mix(finalColor, u_color_blob2, shape2);
    finalColor = mix(finalColor, u_color_blob3, shape4);
    finalColor = mix(finalColor, u_color_blob4, shape3);

    gl_FragColor = finalColor;
}
`;

const gradientSettings = {
  blobs: {
    blob1: {
      position: {
        type: '2f',
        currentValue: [-0.5, 0.5],
        frames: [
          { frame: 0, value: [-0.5, 0.5] },
          { frame: 100, value: [-0.1, 1.1] },
          { frame: 150, value: [-0.1, 1.1] },
          { frame: 280, value: [0.3, -0.2] },
          { frame: 280, value: [-0.2, 1.2] },
          { frame: 310, value: [0.1, 1.05] },
          { frame: 355, value: [0.5, 1.25] },
          { frame: 400, value: [0.9, 1.05] },
        ],
      },
      size: {
        type: '1f',
        currentValue: 0.4,
        frames: [
          { frame: 0, value: 0.4 },
          { frame: 100, value: 0.4 },
          { frame: 150, value: 0.4 },
          { frame: 280, value: 0.5 },
          { frame: 280, value: 0.4 },
        ],
      },
      scale: {
        type: '1f',
        currentValue: 1,
        frames: [{ frame: 0, value: 1 }],
      },
      angle: {
        type: '1f',
        currentValue: 0,
        frames: [{ frame: 0, value: 0 }],
      },
      smooth_step: {
        type: '1f',
        currentValue: 0.6,
        frames: [
          { frame: 0, value: 0.6 },
          { frame: 280, value: 0.6 },
          { frame: 310, value: 0.8 },
        ],
      },
      color: {
        type: '4f',
        currentValue: [0.051, 0.0666, 0.165, 1],
        frames: [
          { frame: 0, value: [0.051, 0.0666, 0.165, 1] },
          { frame: 100, value: [0.337, 0.494, 0.612, 1] },
          { frame: 150, value: [0.337, 0.494, 0.612, 1] },
          { frame: 280, value: [0.055, 0.141, 0.408, 1] },
          { frame: 280, value: [0.051, 0.0666, 0.165, 1] },
        ],
      },
    },
    blob2: {
      position: {
        type: '2f',
        currentValue: [1, 1],
        frames: [
          { frame: 0, value: [1.1, -0.1] },
          { frame: 280, value: [1.1, -0.1] },
          { frame: 310, value: [1.1, -0.15] },
          { frame: 355, value: [0.6, -0.25] },
          { frame: 400, value: [0.1, -0.15] },
          { frame: 480, value: [-0.5, 0.6] },
          { frame: 530, value: [-0.5, 0.6] },
          { frame: 565, value: [0.1, 0.85] },
          { frame: 600, value: [0.5, 1.0] },
        ],
      },
      size: {
        type: '1f',
        currentValue: 0.3,
        frames: [
          { frame: 0, value: 0.3 },
          { frame: 280, value: 0.4 },
          { frame: 530, value: 0.4 },
          { frame: 565, value: 0.5 },
          { frame: 600, value: 0.5 },
        ],
      },
      scale: {
        type: '1f',
        currentValue: 1,
        frames: [{ frame: 0, value: 1 }],
      },
      angle: {
        type: '1f',
        currentValue: 0,
        frames: [{ frame: 0, value: 0 }],
      },
      smooth_step: {
        type: '1f',
        currentValue: 0.6,
        frames: [
          { frame: 0, value: 0.6 },
          { frame: 280, value: 0.6 },
          { frame: 310, value: 0.8 },
          { frame: 530, value: 0.8 },
          { frame: 565, value: 0.6 },
          { frame: 600, value: 0.8 },
        ],
      },
      color: {
        type: '4f',
        currentValue: [0.051, 0.0666, 0.165, 1],
        frames: [
          { frame: 0, value: [0.051, 0.0666, 0.165, 1] },
          { frame: 530, value: [0.051, 0.0666, 0.165, 1] },
          { frame: 530, value: [0.337, 0.494, 0.612, 1] },
          { frame: 600, value: [0.337, 0.494, 0.612, 1] },
        ],
      },
    },
    blob3: {
      position: {
        type: '2f',
        currentValue: [0.6, 0.6],
        frames: [
          {
            frame: 0,
            value: [0.6, 0.6],
            path: {
              type: 'bezier',
              control: [0.769, 0.191, 1.0, 0.5],
            },
          },
          { frame: 100, value: [1.1, 0.8] },
          { frame: 150, value: [1.1, 0.5] },
          { frame: 225, value: [0.7, 0.7] },
          { frame: 280, value: [0.6, 0.6] },
          { frame: 410, value: [0.6, 0.6] },
          { frame: 445, value: [0.8, 0.7] },
          { frame: 480, value: [1.0, 0.6] },
          { frame: 500, value: [1.0, 0.5] },
          { frame: 560, value: [0.5, 1.0] },
        ],
      },
      size: {
        type: '1f',
        currentValue: 0.4,
        frames: [
          { frame: 0, value: 0.4 },
          { frame: 310, value: 0.4 },
          { frame: 380, value: 0.25 },
          { frame: 480, value: 0.4 },
        ],
      },
      scale: {
        type: '1f',
        currentValue: 1,
        frames: [{ frame: 0, value: 1 }],
      },
      angle: {
        type: '1f',
        currentValue: 0,
        frames: [
          { frame: 0, value: 0 },
          { frame: 600, value: 0 },
        ],
      },
      smooth_step: {
        type: '1f',
        currentValue: 0.7,
        frames: [{ frame: 0, value: 0.7 }],
      },
      color: {
        type: '4f',
        currentValue: [0.408, 0.588, 0.702, 1],
        frames: [{ frame: 0, value: [0.408, 0.588, 0.702, 1] }],
      },
    },
    blob4: {
      position: {
        type: '2f',
        currentValue: [0.4, 0.4],
        frames: [
          { frame: 0, value: [0.4, 0.4] },
          { frame: 100, value: [1.1, 0.5] },
          { frame: 150, value: [1.1, 0.2] },
          { frame: 225, value: [0.7, 0.7] },
          { frame: 280, value: [0.4, 0.4] },
          { frame: 410, value: [0.4, 0.4] },
          { frame: 445, value: [0.8, 0.8] },
          { frame: 480, value: [1.0, 0.5] },
          { frame: 500, value: [1.0, 0.5] },
          { frame: 560, value: [0.5, 1.0] },
        ],
      },
      size: {
        type: '1f',
        currentValue: 0.4,
        frames: [
          { frame: 0, value: 0.4 },
          { frame: 310, value: 0.4 },
          { frame: 380, value: 0.25 },
          { frame: 480, value: 0.5 },
        ],
      },
      scale: {
        type: '1f',
        currentValue: 1,
        frames: [{ frame: 0, value: 1 }],
      },
      angle: {
        type: '1f',
        currentValue: 0,
        frames: [{ frame: 0, value: 0 }],
      },
      smooth_step: {
        type: '1f',
        currentValue: 0.8,
        frames: [
          { frame: 0, value: 0.8 },
          { frame: 380, value: 0.8 },
          { frame: 480, value: 0.9 },
        ],
      },
      color: {
        type: '4f',
        currentValue: [0.408, 0.588, 0.702, 1],
        frames: [{ frame: 0, value: [0.408, 0.588, 0.702, 1] }],
      },
    },
  },
  canvas: {
    u_time_multiplier: {
      type: '1f',
      value: 0.5,
    },
    u_deformation_ratio: {
      type: '1f',
      value: 3.0,
    },
    u_deformation_speed_x: {
      type: '1f',
      value: 0.5,
    },
    u_deformation_speed_y: {
      type: '1f',
      value: 0.3,
    },
    u_separation_ratio: {
      type: '1f',
      value: 0.2,
    },
    u_corner_smothing_ratio: {
      type: '1f',
      value: 0.5,
    },
    u_smooth_step: {
      type: '1f',
      value: 0.5,
    },
    u_bg_color: {
      type: '4f',
      value: [0.054901960784313725, 0.1411764705882353, 0.40784313725490196, 1],
    },
    u_deform_blobs: {
      type: '1f',
      value: 1.0,
    },
  },
};

if (!isDesktop()) {
  gradientSettings.canvas.u_deform_blobs.value = 0.1;
  gradientSettings.canvas.u_deformation_ratio.value = 1.0;
}
let gl = null;
let canvas = null;
let program = null;
const uniformLocations = {};
const lastUniformValues = {};

function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createProgram(gl, vSrc, fSrc) {
  const vs = createShader(gl, gl.VERTEX_SHADER, vSrc);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, fSrc);
  if (!vs || !fs) return null;
  const pr = gl.createProgram();
  gl.attachShader(pr, vs);
  gl.attachShader(pr, fs);
  gl.linkProgram(pr);
  if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(pr));
    gl.deleteProgram(pr);
    return null;
  }
  return pr;
}

function cacheUniformLocations(gl, program, gradientSettings) {
  for (const [blobName, blobSettings] of Object.entries(gradientSettings.blobs)) {
    for (const [paramName, paramSpec] of Object.entries(blobSettings)) {
      const uniformName = `u_${paramName}_${blobName}`;
      uniformLocations[uniformName] = gl.getUniformLocation(program, uniformName);
    }
  }
  for (const [name] of Object.entries(gradientSettings.canvas)) {
    const uniformName = name;
    uniformLocations[uniformName] = gl.getUniformLocation(program, uniformName);
  }

  uniformLocations['u_time'] = gl.getUniformLocation(program, 'u_time');
  uniformLocations['u_resolution'] = gl.getUniformLocation(program, 'u_resolution');
}

function setUniformIfNeeded(gl, name, value, type) {
  const loc = uniformLocations[name];
  if (!loc) return;
  const prev = lastUniformValues[name];

  if (type === '1f') {
    if (prev === value) return;
    lastUniformValues[name] = value;
    gl.uniform1f(loc, value);
    return;
  }

  if (type === '2f') {
    if (Array.isArray(prev) && prev[0] === value[0] && prev[1] === value[1]) return;
    lastUniformValues[name] = [value[0], value[1]];
    gl.uniform2f(loc, value[0], value[1]);
    return;
  }

  if (type === '3f') {
    if (Array.isArray(prev) && prev[0] === value[0] && prev[1] === value[1] && prev[2] === value[2]) return;
    lastUniformValues[name] = [value[0], value[1], value[2]];
    gl.uniform3f(loc, value[0], value[1], value[2]);
    return;
  }

  if (type === '4f') {
    if (
      Array.isArray(prev) &&
      prev[0] === value[0] &&
      prev[1] === value[1] &&
      prev[2] === value[2] &&
      prev[3] === value[3]
    )
      return;
    lastUniformValues[name] = [value[0], value[1], value[2], value[3]];
    gl.uniform4f(loc, value[0], value[1], value[2], value[3]);
    return;
  }
}

function uploadUniforms(gl, gradientSettings) {
  for (const [blobName, blobSettings] of Object.entries(gradientSettings.blobs)) {
    for (const [paramName, paramSpec] of Object.entries(blobSettings)) {
      const uniformKey = `u_${paramName}_${blobName}`;
      const type = paramSpec.type;
      const val = paramSpec.currentValue;
      setUniformIfNeeded(gl, uniformKey, val, type);
    }
  }

  for (const [name, spec] of Object.entries(gradientSettings.canvas)) {
    const type = spec.type;
    const val = spec.value;
    setUniformIfNeeded(gl, name, val, type);
  }
}

function evaluatePathSegment(path, from, to, t, dim = 2) {
  if (!from || !to || from.length !== dim || to.length !== dim) return from;
  const type = path?.type || 'linear';
  switch (type) {
    case 'bezier': {
      const cp = path.control || from.map((f, i) => (f + to[i]) / 2);
      const u = 1 - t;
      return from.map((_, i) => u * u * from[i] + 2 * u * t * cp[i] + t * t * to[i]);
    }
    case 'arc': {
      const cx = (from[0] + to[0]) / 2;
      const cy = (from[1] + to[1]) / 2;
      const radiusX = Math.abs(to[0] - from[0]) / 2;
      const radiusY = Math.abs(to[1] - from[1]) / 2;
      const startAngle = Math.atan2(from[1] - cy, from[0] - cx);
      const endAngle = Math.atan2(to[1] - cy, to[0] - cx);
      let deltaAngle = endAngle - startAngle;
      if (deltaAngle <= 0) deltaAngle += 2 * 3.141592653589793;
      const angle = startAngle + deltaAngle * t;
      return [cx + radiusX * Math.cos(angle), cy + radiusY * Math.sin(angle)];
    }
    case 'custom':
      return typeof path.fn === 'function' ? path.fn(from, to, t) : from;
    case 'linear':
    default:
      return from.map((f, i) => f + (to[i] - f) * t);
  }
}

const easings = {
  linear: (t) => t,
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
};

function interpolateValue(frames, frameFloat, type = '1f') {
  for (let i = 0; i < frames.length - 1; i++) {
    const a = frames[i];
    const b = frames[i + 1];
    if (frameFloat >= a.frame && frameFloat <= b.frame) {
      const tRaw = (frameFloat - a.frame) / (b.frame - a.frame);
      const path = a.path || { type: 'linear' };
      const easingFunc = path.easing ? easings[path.easing] || easings.linear : easings.linear;
      const t = easingFunc(tRaw);
      const from = a.value;
      const to = b.value;
      switch (type) {
        case '1f':
          return typeof from === 'number' && typeof to === 'number' ? from + (to - from) * t : 0;
        case '2f':
          return evaluatePathSegment(path, from, to, t, 2);
        case '3f':
          return evaluatePathSegment(path, from, to, t, 3);
        case '4f':
          return evaluatePathSegment(path, from, to, t, 4);
        default:
          return from;
      }
    }
  }
  const last = frames[frames.length - 1].value;
  return type === '1f' ? last : [...last];
}

function getUniform(name) {
  return uniformLocations[name] || null;
}

function addGradientBG() {
  canvas = document.getElementById('gradient-canvas');
  if (!canvas) {
    console.error('Canvas #gradient-canvas not found');
    return;
  }

  const rawDPR = window.devicePixelRatio || 1;
  const baseDPR = isDesktop() ? Math.min(rawDPR, 1.5) : 1;
  const scaleFactor = isDesktop() ? 0.75 : 0.1;

  function resizeCanvasToDisplaySize() {
    const width = Math.round(window.innerWidth * baseDPR * scaleFactor);
    const height = Math.round(window.innerHeight * baseDPR * scaleFactor);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      if (gl) {
        gl.viewport(0, 0, canvas.width, canvas.height);
        const uResLoc = uniformLocations['u_resolution'];
        if (uResLoc) gl.uniform2f(uResLoc, canvas.width, canvas.height);
      }
    }
  }

  gl = canvas.getContext('webgl', {
    alpha: false,
    antialias: true,
    powerPreference: isDesktop() ? 'high-performance' : 'low-power',
  });
  if (!gl) {
    console.error('WebGL not supported');
    return;
  }

  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.BLEND);
  gl.clearColor(0, 0, 0, 1);

  program = createProgram(gl, vShaderSource, fShaderSource);
  if (!program) return;

  gl.useProgram(program);

  const quadBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  const quadVertices = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
  gl.bufferData(gl.ARRAY_BUFFER, quadVertices, gl.STATIC_DRAW);

  const positionLocation = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(positionLocation);
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

  cacheUniformLocations(gl, program, gradientSettings);

  resizeCanvasToDisplaySize();
  window.addEventListener('resize', resizeCanvasToDisplaySize);

  for (const [name, spec] of Object.entries(gradientSettings.canvas)) {
    const loc = uniformLocations[name];
    if (!loc) continue;
    const type = spec.type;
    const val = spec.value;
    switch (type) {
      case '1f':
        gl.uniform1f(loc, val);
        lastUniformValues[name] = val;
        break;
      case '2f':
        gl.uniform2f(loc, val[0], val[1]);
        lastUniformValues[name] = [val[0], val[1]];
        break;
      case '3f':
        gl.uniform3f(loc, val[0], val[1], val[2]);
        lastUniformValues[name] = [val[0], val[1], val[2]];
        break;
      case '4f':
        gl.uniform4f(loc, val[0], val[1], val[2], val[3]);
        lastUniformValues[name] = [val[0], val[1], val[2], val[3]];
        break;
    }
  }

  const uResolutionLoc = uniformLocations['u_resolution'];
  if (uResolutionLoc) gl.uniform2f(uResolutionLoc, canvas.width, canvas.height);
  lastUniformValues['u_resolution'] = [canvas.width, canvas.height];

  function updateGradient() {
    uploadUniforms(gl, gradientSettings);
  }

  function updateGradientSettingsByFrame(progress, blobsFraming, maxFrame) {
    const frameFloat = progress * maxFrame;
    const blobs = blobsFraming;
    for (const [blobName, blobSettings] of Object.entries(blobs)) {
      for (const [paramName, paramData] of Object.entries(blobSettings)) {
        if (!paramData.frames) continue;
        const type = gradientSettings.blobs[blobName][paramName].type;
        gradientSettings.blobs[blobName][paramName].currentValue = interpolateValue(paramData.frames, frameFloat, type);
      }
    }
    updateGradient();
  }

  let lastTime = performance.now();
  let isPaused = false;
  let requested = null;
  const desiredFPS = isDesktop() ? 24 : 20;
  const minFrameInterval = 1000 / desiredFPS;

  function render(now) {
    requested = null;
    if (isPaused) return;
    const dt = now - lastTime;
    if (dt < minFrameInterval) {
      requested = requestAnimationFrame(render);
      return;
    }
    lastTime = now;

    gl.clear(gl.COLOR_BUFFER_BIT);

    const uTimeLoc = uniformLocations['u_time'];
    if (uTimeLoc) {
      const timeSec = now * 0.0011;
      setUniformIfNeeded(gl, 'u_time', timeSec, '1f');
    }

    updateGradient();

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    requested = requestAnimationFrame(render);
  }

  function stopGradient() {
    isPaused = true;
    if (requested) {
      cancelAnimationFrame(requested);
      requested = null;
    }
  }
  function resumeGradient() {
    if (!isPaused) return;
    isPaused = false;
    lastTime = performance.now();
    if (!requested) requested = requestAnimationFrame(render);
  }

  requested = requestAnimationFrame(render);

  const handleVisibilityChange = () => {
    if (isGradientPlaybackManaged) {
      applyGradientPlaybackState();
      return;
    }

    if (document.hidden) {
      stopGradient();
    } else {
      resumeGradient();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return {
    stopGradient,
    resumeGradient,
    updateGradientSettingsByFrame,
  };
}

const switchGradient = () => {
  const sections = $('section, #shopify-section-footer');
  sections.each(function () {
    const sectionBG = $(this).find('.section-bg');
    const isLight = sectionBG.length > 0 ? checkBG(sectionBG) : checkBG($(this));
    const trigger = $(this);
    const options = {
      trigger,
      start: isLight ? 'top top' : 'top bottom',
      end: isLight ? 'bottom bottom' : 'bottom top',
      onEnter: isLight ? gradientAPI.stopGradient : gradientAPI.resumeGradient,
      onEnterBack: isLight ? gradientAPI.stopGradient : gradientAPI.resumeGradient,
    };
    ScrollTrigger.create(options);
  });
};

window.addEventListener('load', () => {
  if (!isLowEndDevice) {
    gradientAPI = addGradientBG();
    applyGradientPlaybackState();
  }
  //switchGradient();
});
